import React, { memo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cn from 'classnames';
// Components
import { AffirmPromo, AFFIRM_PROMO_TYPES } from 'src/components/Affirm';
import { Badge } from 'src/components/Elements/Badge';

import ProductPriceGuarantee from 'src/components/Product/ProductHeader/ProductPriceGuarantee';

const ProductPriceMember = ({ cheapestPrice, memberPrices, priceStyles, priceClasses }) => {
  // If there is no membership discount then do not show base price with line-through and do not show the "Member Price" badge
  const hasMembershipDiscount = memberPrices.cheapestPrice;
  const cheapestPriceStyles = cn({ 'text-weight-regular n300 line-through': hasMembershipDiscount });
  return (
    <div className={cn('flex alignItemsCenter', priceStyles)}>
      <h5 className={priceClasses}>
        <span className={cheapestPriceStyles}>${cheapestPrice}</span>
        {memberPrices.cheapestPriceFormatted && (
          <span className="blue700">&nbsp;{memberPrices.cheapestPriceFormatted}</span>
        )}
      </h5>
      {hasMembershipDiscount && (
        <Badge size="small" color="blue" variant="solid" classes="marginLeft-tiny1">
          Member Price
        </Badge>
      )}
    </div>
  );
};
const ProductPriceOriginal = ({ cheapestPrice, memberPrices, priceStyles, priceClasses }) => {
  return (
    <div className={cn('flex alignItemsCenter', priceStyles)}>
      <h5 className={priceClasses}>
        ${cheapestPrice}
        {memberPrices.cheapestPriceFormatted && (
          <>
            <span className="text-weight-regular">&nbsp;|&nbsp;</span>{' '}
            <span className={cn('blue700 text-weight-regular')}>Member Price</span>
            <span className="blue700">&nbsp;{memberPrices.cheapestPriceFormatted}</span>
          </>
        )}
      </h5>
    </div>
  );
};

const ProductPrice = ({
  service,
  subscriptionPrices = {},
  isProductVariant,
  showAffirm, // manual control for show/hiding the Affirm
  showRetailPrice,
  isMember,
  showPriceGuarantee,
  priceStyles,
  priceClasses,
}) => {
  const {
    listPrice: { formatted: formattedListPrice, value: listPriceValue },
    cheapestPrice,
  } = service;
  const {
    product: prodMemberPrices = {},
    productWithInstallation: prodMemberWithInstallationPrices = {},
  } = subscriptionPrices;

  const affirmPrice = get(service, 'basePrice');
  const memberPrices = isProductVariant ? prodMemberWithInstallationPrices : prodMemberPrices;

  const ProductPriceVersion = isMember ? ProductPriceMember : ProductPriceOriginal;

  const showAffirmPromo = showAffirm && !isMember;
  return (
    <>
      {showPriceGuarantee && (
        <ProductPriceGuarantee className="marginTop-small1 marginBottom-tiny" />
      )}
      <ProductPriceVersion
        cheapestPrice={cheapestPrice}
        memberPrices={memberPrices}
        priceStyles={priceStyles}
        priceClasses={priceClasses}
      />
      {showRetailPrice && listPriceValue && (
        <p className="caption n700 marginBottom-tiny1">
          <span className="line-through">{formattedListPrice}</span> Retail Price
        </p>
      )}
      {showAffirmPromo && (
        <AffirmPromo
          className="p2 marginBottom-small2"
          type={AFFIRM_PROMO_TYPES.product}
          amount={affirmPrice}
        />
      )}
    </>
  );
};

const sharedPropTypes = {
  cheapestPrice: PropTypes.number,
  memberPrices: PropTypes.object,
  priceStyles: PropTypes.string,
  priceClasses: PropTypes.string,
};
ProductPriceOriginal.propTypes = sharedPropTypes;
ProductPriceMember.propTypes = sharedPropTypes;

ProductPrice.propTypes = {
  service: PropTypes.object,
  subscriptionPrices: PropTypes.shape({
    product: PropTypes.object,
    productwithInstallation: PropTypes.object,
  }),
  isProductVariant: PropTypes.bool,
  showAffirm: PropTypes.bool,
  showRetailPrice: PropTypes.bool,
  isMember: PropTypes.bool,
  showPriceGuarantee: PropTypes.bool,
  priceStyles: PropTypes.string,
  priceClasses: PropTypes.string,
};

export default memo(ProductPrice);
